import { useMemo, useState } from "react";
import { Download, ExternalLink, FileDown, Image as ImageIcon, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { fetchEvent, posterImageUrl, posterUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const SIZES = [
  { id: "A4", label: "A4", hint: "Oppslagstavle, 21 × 30 cm", ratio: "aspect-[1/1.414]" },
  { id: "A3", label: "A3", hint: "Stor plakat, 30 × 42 cm", ratio: "aspect-[1/1.414]" },
  { id: "square", label: "Instagram", hint: "Kvadrat 1080 × 1080", ratio: "aspect-square" },
  { id: "story", label: "Story", hint: "9:16 · 1080 × 1920", ratio: "aspect-[9/16]" },
];
const STYLES = [
  { id: "foto", label: "Foto", hint: "Biletet øvst, orda under", needsPhoto: true },
  { id: "fullbreidd", label: "Fullbreidd", hint: "Biletet fyller alt, orda på eit mørkt slør", needsPhoto: true },
  { id: "typo", label: "Typografisk", hint: "Datoen vert biletet – treng ikkje foto" },
];
const THEMES = [{ id: "papir", label: "Papir" }, { id: "natt", label: "Natt" }];

const Seg = ({ items, value, onChange, testId, render }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid={testId}>
    {items.map((it) => (
      <button key={it.id} type="button" role="radio" aria-checked={value === it.id} disabled={it.disabled} onClick={() => onChange(it.id)} title={it.hint}
        className={cn("h-9 rounded-full border px-3.5 text-[13px] font-medium transition-colors disabled:opacity-40", value === it.id ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground/40")} data-testid={`${testId}-${it.id}`}>
        {render ? render(it) : it.label}
      </button>
    ))}
  </div>
);

/**
 * Pick a format, a style and a tone, see the poster update live, download the print PDF or the social image.
 * Everything is rendered on the server from the event itself, so it is always up to date with the page.
 */
export const PosterDialog = ({ eventId, hasPhoto: hasPhotoProp, open, onOpenChange, title }) => {
  const { data: ev } = useQuery({ queryKey: ["event", eventId], queryFn: () => fetchEvent(eventId), enabled: open && hasPhotoProp === undefined, staleTime: 60_000 });
  const hasPhoto = hasPhotoProp !== undefined ? hasPhotoProp : !!(ev?.images?.length || ev?.image);
  const [size, setSize] = useState("A4");
  const [styleChoice, setStyleChoice] = useState(null);
  const style = styleChoice || (hasPhoto ? "foto" : "typo");
  const setStyle = setStyleChoice;
  const [theme, setTheme] = useState("papir");
  const [flags, setFlags] = useState({ qr: true, price: true, people: true });
  const [loading, setLoading] = useState(true);
  const opts = useMemo(() => ({ size, style, theme, ...flags }), [size, style, theme, flags]);
  const preview = posterImageUrl(eventId, { ...opts, w: 720 });
  const print = size === "A4" || size === "A3";
  const sizeMeta = SIZES.find((s) => s.id === size);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-card p-0 sm:max-w-4xl" data-testid="poster-dialog">
        <div className="grid md:grid-cols-[minmax(0,1fr)_320px]">
          <div className="sticky top-0 z-10 flex items-center justify-center bg-secondary/95 p-4 backdrop-blur-md max-md:max-h-[44vh] md:static md:bg-secondary/70 md:p-8" data-testid="poster-preview-pane">
            <div className={cn("relative w-full max-w-[380px] overflow-hidden rounded-[6px] shadow-[0_30px_60px_-30px_rgba(28,25,23,.5)] ring-1 ring-black/5 max-md:max-h-[40vh] max-md:w-auto", sizeMeta.ratio, size === "story" && "max-w-[260px]")}>
              {loading && <div className="absolute inset-0 flex items-center justify-center bg-card/70"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
              <img key={preview} src={preview} alt={`Førehandsvising av plakat – ${title || ""}`} onLoad={() => setLoading(false)} onLoadStart={() => setLoading(true)} onError={() => setLoading(false)} className="h-full w-full object-cover max-md:h-[40vh] max-md:w-auto" data-testid="poster-preview-image" />
            </div>
          </div>
          <div className="p-6 md:border-l md:border-border">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-[20px] tracking-[-0.015em]">Plakat</DialogTitle>
              <DialogDescription className="text-[13px]">Laga av tittel, dato, bilete og QR-kode – alltid oppdatert med sida.</DialogDescription>
            </DialogHeader>
            <div className="mt-5 space-y-5">
              <div>
                <p className="t-mono mb-2 text-muted-foreground">Format</p>
                <Seg items={SIZES} value={size} onChange={(v) => { setLoading(true); setSize(v); }} testId="poster-size" />
                <p className="mt-1.5 text-[12px] text-muted-foreground">{sizeMeta.hint}</p>
              </div>
              <div>
                <p className="t-mono mb-2 text-muted-foreground">Stil</p>
                <Seg items={STYLES.map((s) => ({ ...s, disabled: s.needsPhoto && !hasPhoto }))} value={style} onChange={(v) => { setLoading(true); setStyle(v); }} testId="poster-style" />
                <p className="mt-1.5 text-[12px] text-muted-foreground">{STYLES.find((s) => s.id === style)?.hint}{!hasPhoto && " · Legg til eit bilete på arrangementet for fotostilane."}</p>
              </div>
              <div>
                <p className="t-mono mb-2 text-muted-foreground">Tone</p>
                <Seg items={THEMES} value={theme} onChange={(v) => { setLoading(true); setTheme(v); }} testId="poster-theme" />
              </div>
              <div className="space-y-2.5 border-t border-border pt-4">
                {[["qr", "QR-kode til påmelding"], ["price", "Pris og GRATIS-stempel"], ["people", "Medverkande"]].map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between gap-3 text-[13.5px]"><span>{label}</span><Switch checked={flags[k]} onCheckedChange={(v) => { setLoading(true); setFlags((f) => ({ ...f, [k]: !!v })); }} data-testid={`poster-flag-${k}`} /></label>
                ))}
              </div>
              <div className="space-y-2 border-t border-border pt-4">
                {print && <Button asChild className="h-11 w-full"><a href={posterUrl(eventId, { ...opts, download: 1 })} data-testid="poster-download-pdf"><FileDown className="h-4 w-4" /> Last ned PDF ({size})</a></Button>}
                <Button asChild variant={print ? "outline" : "default"} className={cn("h-11 w-full", print && "bg-transparent")}><a href={posterImageUrl(eventId, { ...opts, download: 1 })} data-testid="poster-download-jpg"><ImageIcon className="h-4 w-4" /> Last ned bilete (JPG)</a></Button>
                <div className="flex gap-2">
                  <Button asChild variant="ghost" className="h-9 flex-1 text-[13px]"><a href={print ? posterUrl(eventId, opts) : posterImageUrl(eventId, opts)} target="_blank" rel="noreferrer" data-testid="poster-open-new-tab"><ExternalLink className="h-3.5 w-3.5" /> Opne i ny fane</a></Button>
                </div>
                <p className="flex items-start gap-1.5 text-[12px] text-muted-foreground"><Download className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {print ? "PDF-en er klar for utskrift hos Coop eller på kontoret." : "Biletet er i rett storleik for Instagram og Facebook – legg det rett ut."}</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
